import React, { Component } from "react";
import { Link, NavLink } from "react-router-dom";
import { stegaClean } from "@sanity/client/stega";
import { urlFor } from "../lib/sanity";

/**
 * Build a ui-avatars.com fallback URL from a person's name. Strips stega
 * (Content Source Map) characters first — in the Presentation iframe those
 * invisible chars are appended to every string and would corrupt the URL,
 * which is why generated avatars failed to load in preview.
 */
export function avatarUrl(name?: string) {
  const clean = (stegaClean(name) || "").toString().trim() || "Brown Esports";
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    clean
  )}&color=cccccc&background=333333`;
}

type ControlledLinkProps = {
  href: string;
  className: string;
  navlink?: boolean;
  activeClassName?: string;
  children?: React.ReactNode;
};

export function formatPublicLink(url: string) {
  if (
    url &&
    !url.includes("http://") &&
    !url.includes("https://") &&
    url.charAt(0) === "/"
  ) {
    return process.env.PUBLIC_URL + url;
  } else {
    return url;
  }
}

export function isExternalLink(url: string) {
  if (
    url &&
    !url.includes("http://") &&
    !url.includes("https://") &&
    url.charAt(0) === "/"
  ) {
    return false;
  } else if (url === "") {
    return null;
  }
  return true;
}

/**
 * Resolve an image source to a URL. Accepts a Sanity image object/reference
 * (built via the CDN image-url builder) and falls back to `default_image`
 * (e.g. a ui-avatars URL) or a bundled placeholder when none is provided.
 */
export function imageIdToURL(
  image: any,
  default_image?: string | File
) {
  if (image) {
    try {
      return urlFor(image).url() as string;
    } catch (e) {
      // not a valid Sanity image source — fall through to the defaults below
    }
  }
  // return default image if the source is null/undefined, only if provided
  if (default_image) {
    return default_image;
  }
  // fallback to bundled placeholder
  return require("../components/assets/bg11.jpg");
}

export class ControlledLink extends Component<ControlledLinkProps> {
  render() {
    let ext = isExternalLink(this.props.href);
    if (ext) {
      return (
        <a href={this.props.href} className={this.props.className}>
          {this.props.children}
        </a>
      );
    } else if (ext === null) {
      <div className={this.props.className}>{this.props.children}</div>;
    } else if (this.props.navlink) {
      return (
        <NavLink
          end
          to={this.props.href}
          className={({ isActive }) =>
            isActive && this.props.activeClassName
              ? this.props.activeClassName
              : this.props.className
          }
        >
          {this.props.children}
        </NavLink>
      );
    }
    return (
      <Link to={this.props.href} className={this.props.className}>
        {this.props.children}
      </Link>
    );
  }
}
